import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Button, Card, Descriptions, Input, Space, Spin, Typography, message } from 'antd';
import { ReloadOutlined, ThunderboltOutlined, MailOutlined, SafetyOutlined, WhatsAppOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const formatCurrency = (value) => {
  const n = Number(value || 0);
  return n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
};

const grantKey = (token) => `autofactura_grant_${token}`;

const AutofacturaPortal = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  // Paso OTP.
  const [otpRequested, setOtpRequested] = useState(false);
  const [phoneHint, setPhoneHint] = useState('');
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [grant, setGrant] = useState(() => {
    try {
      return sessionStorage.getItem(grantKey(token)) || '';
    } catch (e) {
      return '';
    }
  });
  const cooldownRef = useRef(null);

  const persistGrant = (value) => {
    setGrant(value);
    try {
      if (value) sessionStorage.setItem(grantKey(token), value);
      else sessionStorage.removeItem(grantKey(token));
    } catch (e) {
      /* sessionStorage no disponible: el grant vive solo en memoria */
    }
  };

  const startCooldown = (seconds) => {
    setCooldown(seconds || 0);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => () => {
    if (cooldownRef.current) clearInterval(cooldownRef.current);
  }, []);

  const fetchPreview = async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const resp = await fetch(`/api/autofactura/${encodeURIComponent(token)}?json=1`, {
        headers: { Accept: 'application/json' },
      });
      const json = await resp.json();
      if (!resp.ok) {
        throw new Error(json?.error || 'No se pudo cargar el portal');
      }
      setData(json?.data || null);
    } catch (e) {
      setData(null);
      setError(e?.message || 'No se pudo cargar el portal');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const alreadyGenerated = Boolean(data?.already_generated);
  const invoiceUuid = data?.invoice?.uuid || '';
  const txMonto = data?.transaction?.monto ?? null;
  const configMonto = data?.config?.monto ?? null;
  const phonePresent = Boolean(data?.config?.receiver_phone_present);

  const header = useMemo(() => {
    const periodo = data?.periodo || '—';
    return (
      <Space direction="vertical" size={2}>
        <Title level={3} style={{ margin: 0 }}>
          Autoportal de facturación
        </Title>
        <Text type="secondary">Periodo: {periodo}</Text>
      </Space>
    );
  }, [data?.periodo]);

  const requestCode = async () => {
    if (!token) return;
    setActionLoading(true);
    try {
      const resp = await fetch(`/api/autofactura/${encodeURIComponent(token)}/otp/request`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
      });
      const json = await resp.json();
      if (!resp.ok) {
        throw new Error(json?.error || 'No se pudo enviar el código');
      }
      setOtpRequested(true);
      setPhoneHint(json?.data?.phone_hint || '');
      startCooldown(json?.data?.cooldown_seconds || 60);
      message.success('Código enviado por WhatsApp');
    } catch (e) {
      message.error(e?.message || 'No se pudo enviar el código');
    } finally {
      setActionLoading(false);
    }
  };

  const verifyCode = async () => {
    if (!token) return;
    const clean = String(code || '').trim();
    if (!clean) {
      message.error('Ingresa el código que recibiste');
      return;
    }
    setActionLoading(true);
    try {
      const body = new URLSearchParams();
      body.set('code', clean);
      const resp = await fetch(`/api/autofactura/${encodeURIComponent(token)}/otp/verify`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        },
        body: body.toString(),
      });
      const json = await resp.json();
      if (!resp.ok) {
        throw new Error(json?.error || 'Código incorrecto o expirado');
      }
      persistGrant(json?.data?.grant || '');
      setCode('');
      message.success('Autorización concedida');
    } catch (e) {
      message.error(e?.message || 'Código incorrecto o expirado');
    } finally {
      setActionLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!token) return;
    if (!alreadyGenerated && !grant) {
      message.error('Primero autoriza con el código enviado por WhatsApp');
      return;
    }
    setActionLoading(true);
    try {
      const body = new URLSearchParams();
      if (grant) body.set('grant', grant);
      const resp = await fetch(`/api/autofactura/${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        },
        body: body.toString(),
      });
      const json = await resp.json();
      if (!resp.ok) {
        // 428: la ventana de autorización expiró; pedir de nuevo.
        if (resp.status === 428) {
          persistGrant('');
          setOtpRequested(false);
          throw new Error('La autorización expiró. Solicita un nuevo código');
        }
        throw new Error(json?.error || 'No se pudo generar la factura');
      }
      message.success(json?.message || 'Factura generada');
      await fetchPreview();
    } catch (e) {
      message.error(e?.message || 'No se pudo generar la factura');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendInvoice = async () => {
    if (!token) return;
    setActionLoading(true);
    try {
      const resp = await fetch(`/api/autofactura/${encodeURIComponent(token)}/send-invoice`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
      });
      const json = await resp.json();
      if (!resp.ok) {
        throw new Error(json?.error || 'No se pudo enviar la factura');
      }
      message.success(json?.message || 'Factura enviada');
    } catch (e) {
      message.error(e?.message || 'No se pudo enviar la factura');
    } finally {
      setActionLoading(false);
    }
  };

  const renderAuthStep = () => {
    if (!phonePresent) {
      return (
        <Alert
          type="warning"
          showIcon
          message="Falta el teléfono del receptor"
          description="Para autorizar por WhatsApp, primero captura el teléfono del cliente desde el panel. Sin él no es posible timbrar."
          style={{ marginBottom: 16 }}
        />
      );
    }

    if (grant) {
      return (
        <Alert
          type="success"
          showIcon
          icon={<SafetyOutlined />}
          message="Autorizado"
          description="Ya puedes generar la factura. La autorización expira en unos minutos."
          style={{ marginBottom: 16 }}
        />
      );
    }

    if (!otpRequested) {
      return (
        <Alert
          type="info"
          showIcon
          message="Autoriza para timbrar"
          description="Te enviaremos un código por WhatsApp al teléfono registrado. Ingrésalo para poder generar la factura."
          style={{ marginBottom: 16 }}
          action={
            <Button
              type="primary"
              icon={<WhatsAppOutlined />}
              loading={actionLoading}
              onClick={requestCode}
            >
              Enviar código
            </Button>
          }
        />
      );
    }

    return (
      <Card size="small" style={{ marginBottom: 16 }} title="Ingresa el código de WhatsApp">
        {phoneHint ? (
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            Enviado al número que termina en {phoneHint}
          </Text>
        ) : null}
        <Space wrap>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Código de 6 dígitos"
            maxLength={6}
            inputMode="numeric"
            style={{ width: 180 }}
            onPressEnter={verifyCode}
          />
          <Button type="primary" icon={<SafetyOutlined />} loading={actionLoading} onClick={verifyCode}>
            Verificar
          </Button>
          <Button
            icon={<ReloadOutlined />}
            disabled={cooldown > 0 || actionLoading}
            onClick={requestCode}
          >
            {cooldown > 0 ? `Reenviar en ${cooldown}s` : 'Reenviar'}
          </Button>
        </Space>
      </Card>
    );
  };

  return (
    <div style={{ padding: 24, maxWidth: 900, margin: '0 auto' }}>
      <Card title={header}>
        {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} /> : null}

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
            <Spin />
          </div>
        ) : (
          <>
            <Alert
              type={alreadyGenerated ? 'success' : 'info'}
              showIcon
              message={alreadyGenerated ? 'Factura ya generada para este periodo' : 'Listo para generar'}
              style={{ marginBottom: 16 }}
            />

            <Descriptions bordered size="small" column={1} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Concepto">{data?.config?.concepto || '—'}</Descriptions.Item>
              <Descriptions.Item label="Monto">
                {formatCurrency(alreadyGenerated ? (txMonto ?? data?.invoice?.total ?? 0) : (configMonto ?? 0))}
              </Descriptions.Item>
              <Descriptions.Item label="UUID">{invoiceUuid || '—'}</Descriptions.Item>
            </Descriptions>

            {!alreadyGenerated ? renderAuthStep() : null}

            <Space wrap>
              <Button icon={<ReloadOutlined />} onClick={fetchPreview} disabled={loading || actionLoading}>
                Actualizar
              </Button>
              <Button
                type="primary"
                icon={<ThunderboltOutlined />}
                onClick={handleGenerate}
                loading={actionLoading}
                disabled={loading || (!alreadyGenerated && !grant)}
              >
                Generar factura
              </Button>
              <Button
                icon={<MailOutlined />}
                onClick={handleSendInvoice}
                loading={actionLoading}
                disabled={!alreadyGenerated || !invoiceUuid || loading}
              >
                Enviar por correo
              </Button>
            </Space>
          </>
        )}
      </Card>
    </div>
  );
};

export default AutofacturaPortal;
